import React, { useState } from 'react'
import { Box, Paper, Typography, Button, Chip, CircularProgress } from '@mui/material'
import { HourglassTop as HourglassIcon, Email as EmailIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { usePaymentContext } from '../../contexts/PaymentContext'
import { paymentsApi } from '../../services/api'
import { useNotification } from '../../contexts/NotificationContext'

interface PendingPaymentBannerProps {
  sx?: object
}

export const PendingPaymentBanner: React.FC<PendingPaymentBannerProps> = ({ sx }) => {
  const { pendingTransaction, refreshBilling } = usePaymentContext()
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const [checking, setChecking] = useState(false)

  if (!pendingTransaction) return null

  const scrollToHistory = () => {
    const el = document.getElementById('billing-history')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleCheckStatus = async () => {
    setChecking(true)
    try {
      const res = await paymentsApi.getPaymentStatus(pendingTransaction.transaction_id)
      if (res.status === 'paid' || res.status === 'active') {
        showNotification('Payment confirmed! Your plan is now active.', 'success')
        await refreshBilling()
      } else {
        showNotification('Payment is still processing. Please check again shortly.', 'info')
      }
    } catch {
      showNotification('Could not reach payment server. Please try again.', 'error')
    } finally {
      setChecking(false)
      // Navigate to billing and scroll to history table
      if (window.location.pathname === '/billing') {
        scrollToHistory()
      } else {
        navigate('/billing')
        setTimeout(scrollToHistory, 400)
      }
    }
  }

  const handleContactSupport = () => {
    window.open('mailto:support@lyftr.ai?subject=Payment%20Processing%20Issue', '_blank')
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        py: 1.5,
        px: { xs: 2, sm: 2.5 },
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe',
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(23, 37, 84, 0.4)' : '#eff6ff',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Chip
          icon={<HourglassIcon sx={{ fontSize: '0.85rem !important', color: '#ffffff !important' }} />}
          label="PAYMENT PENDING"
          size="small"
          sx={{
            fontWeight: 800,
            fontSize: '0.75rem',
            letterSpacing: '0.5px',
            bgcolor: '#3b82f6',
            color: '#ffffff',
            height: 24,
            borderRadius: 1,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: (theme) => (theme.palette.mode === 'dark' ? '#93c5fd' : '#1e40af'),
            fontSize: '0.875rem',
            lineHeight: 1.4,
          }}
        >
          We are still processing your payment. This can take 24–48 hours.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap', alignSelf: { xs: 'stretch', sm: 'center' } }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleCheckStatus}
          disabled={checking}
          startIcon={checking ? <CircularProgress size={12} color="inherit" /> : undefined}
          disableElevation
          sx={{
            fontWeight: 800,
            fontSize: '0.8125rem',
            textTransform: 'none',
            borderRadius: 2.25,
            px: 2.25,
            py: 0.75,
            flex: { xs: 1, sm: 'none' },
            bgcolor: '#3b82f6',
            color: '#ffffff',
            boxShadow: '0 2px 6px rgba(59, 130, 246, 0.25)',
            '&:hover': { bgcolor: '#2563eb', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.35)' },
            '&.Mui-disabled': { bgcolor: '#60a5fa', color: 'rgba(255,255,255,0.7)' },
          }}
        >
          {checking ? 'Checking...' : 'Check Status'}
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={handleContactSupport}
          startIcon={<EmailIcon sx={{ fontSize: '0.95rem !important' }} />}
          disableElevation
          sx={{
            fontWeight: 700,
            fontSize: '0.8125rem',
            textTransform: 'none',
            borderRadius: 2.25,
            px: 2,
            py: 0.75,
            flex: { xs: 1, sm: 'none' },
            borderColor: '#93c5fd',
            color: '#1e40af',
            '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.08)', borderColor: '#3b82f6' },
          }}
        >
          Contact Support
        </Button>
      </Box>
    </Paper>
  )
}

export default PendingPaymentBanner
